import React, { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import GrowDiagonalIcon from '@atlaskit/icon/core/grow-diagonal';
import CustomItem from '@atlaskit/menu/custom-item';
import type { CustomItemComponentProps } from '@atlaskit/menu/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { useSmartLinkActions } from '@atlaskit/smart-card/hook/use-smart-link-actions';
import { useSmartLinkReload } from '@atlaskit/smart-card/hook/use-smart-link-reload';
import Tile from '@atlaskit/tile/tile';
import { token } from '@atlaskit/tokens';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { LoadableLazy } from '@confluence/loadable';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { scrollToElement } from '@confluence/scroll/entry-points/scrollToElement.ts';

import {
	ANALYTICS_SOURCE,
	MENU_ITEM_ACTION_SUBJECT_ID,
	SCROLL_RESULT_ACTION_SUBJECT_ID,
	type SmartLinkScrollResult,
} from './analytics.ts';
import type { ConnectionItemHandler, RefreshLinkUrlHandler } from './ConnectionsByLineMenu.tsx';
import { useConnectSmartLinkAccount } from './hooks/useConnectSmartLinkAccount.ts';
import {
	findRenderedSmartLinkElement,
	getSmartLinkContentRoot,
} from './utils/findRenderedSmartLinkElement.ts';
import { SmartLinkIconRenderer } from './SmartLinkIconRenderer.tsx';
import type { EnhancedSmartLink } from './utils/smartLinkTypes.ts';
import { getDescription } from './utils/getDescription.ts';
import { getSmartLinkIcon } from './utils/smartLinkIcon.ts';

const UnconnectedConnectorHoverCardLoadable = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-UnconnectedConnectorHoverCard" */ './UnconnectedConnectorHoverCard.tsx'
			)
		).UnconnectedConnectorHoverCard,
});

const i18n = defineMessages({
	connect: {
		id: 'byline-connections.menu.item.connect',
		defaultMessage: 'Connect',
		description:
			'Action label shown on the right of an unconnected connections menu item, prompting the user to connect the related product.',
	},
	connecting: {
		id: 'byline-connections.menu.item.connecting',
		defaultMessage: 'Connecting...',
		description:
			'Status label shown on the right of an unconnected connections menu item while the related product account is being connected.',
	},
	openPreview: {
		id: 'byline-connections.menu.item.open-preview',
		defaultMessage: 'Open preview',
		description:
			'Aria-label / tooltip for the button that opens an in-product preview of a connected connections menu item, shown on hover.',
	},
	descriptionSeparator: {
		id: 'byline-connections.menu.item.description-separator',
		defaultMessage: '{left} • {right}',
		description:
			'Joins two parts of a connections menu item secondary line with a bullet separator (e.g. resource type, who, and when).',
	},
});

const styles = cssMap({
	textColumn: {
		minWidth: 0,
		flex: 1,
	},
	title: {
		font: token('font.body'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	description: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.regular'),
		color: token('color.text.subtlest'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	trailing: {
		flexShrink: 0,
		display: 'inline-flex',
		alignItems: 'center',
	},
});

const pressableStyles = cssMap({
	connect: {
		font: token('font.body'),
		color: token('color.link'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		cursor: 'pointer',
		paddingBlock: token('space.0'),
		paddingInline: token('space.050'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	connecting: {
		font: token('font.body'),
		color: token('color.text.disabled'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		cursor: 'default',
		paddingBlock: token('space.0'),
		paddingInline: token('space.050'),
		textDecoration: 'none',
	},
});

const PREVIEW_ACTION_ID = 'preview-content';

type RowContent = {
	titleContent: string | undefined;
	descriptionContent: string | undefined | null;
};

/**
 * Builds the title/description for a non-granted byline row (unauthorized,
 * forbidden, not_found, or unresolved). The link resolver doesn't return a
 * usable title/description for these, so we derive one from the URL itself:
 * the path becomes the title and the hostname becomes the description.
 *
 *   https://example.com/foo/bar?x=1#h  →  { titleContent: 'foo/bar?x=1#h', descriptionContent: 'example.com' }
 *   https://www.example.com/           →  { titleContent: 'example.com',  descriptionContent: 'example.com' }
 *   'not a url'                         →  { titleContent: 'not a url',    descriptionContent: null }
 */
const getNonGrantedRowContent = (rawUrl: string): RowContent => {
	let parsed: URL;
	try {
		parsed = new URL(rawUrl);
	} catch {
		// URL was unparseable — fall back to showing the raw URL.
		return { titleContent: rawUrl, descriptionContent: null };
	}

	const hostname = parsed.hostname.replace(/^www\./i, '');
	// Only trim slashes from the pathname itself — slashes inside the query
	// or fragment are meaningful and must be preserved.
	const trimmedPath = parsed.pathname.replace(/^\/+|\/+$/g, '');
	const pathDisplay = `${trimmedPath}${parsed.search}${parsed.hash}` || hostname;

	return { titleContent: pathDisplay, descriptionContent: hostname };
};

/**
 * Builds the title/description for a granted byline row from the data the
 * link resolver returned (title, plus 1–2 description slots).
 */
const getGrantedRowContent = (
	item: EnhancedSmartLink,
	locale: string,
	formatMessage: IntlShape['formatMessage'],
): RowContent => {
	const parts = getDescription(item, locale).filter((slot): slot is string => Boolean(slot));
	const descriptionContent =
		parts.length === 0
			? null
			: parts.reduce((left, right) => formatMessage(i18n.descriptionSeparator, { left, right }));
	return { titleContent: item.title, descriptionContent };
};

const reportPreviewActionError = (err: unknown) => {
	const errorObj = err instanceof Error ? err : new Error(String(err));
	getMonitoringClient().submitError(errorObj, {
		attribution: Attribution.INTEGRATIONS,
	});
};

type RowComponentProps = CustomItemComponentProps & {
	onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
	onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
};

// `CustomItem`'s `component` prop is typed as
// `ComponentType<PropsWithChildren<TCustomComponentProps>>`, but `forwardRef`
// produces `ForwardRefExoticComponent<Omit<Props, "ref"> & RefAttributes<…>>`
// which strips `ref` from the props and so doesn't structurally satisfy
// `ComponentType<Props>`. We cast back to `ComponentType<…>` here: the cast
// is sound because `CustomItem` reads `ref` separately from props and
// forwards it via React's ref-forwarding mechanism (which is exactly what
// `forwardRef` is designed for).
const RowComponent = forwardRef<HTMLDivElement, RowComponentProps>(
	({ children, ...props }, ref) => (
		<div ref={ref} {...props}>
			{children}
		</div>
	),
) as unknown as React.ComponentType<React.PropsWithChildren<RowComponentProps>>;

type ConnectionsByLinePreviewButtonProps = {
	label: string;
	onClick: React.MouseEventHandler<HTMLButtonElement>;
};

const ConnectionsByLinePreviewButton = ({
	label,
	onClick,
}: ConnectionsByLinePreviewButtonProps) => (
	<IconButton
		appearance="subtle"
		spacing="compact"
		icon={GrowDiagonalIcon}
		label={label}
		isTooltipDisabled={false}
		onClick={onClick}
	/>
);

type ConnectButtonProps = {
	item: EnhancedSmartLink;
	refreshLinkUrl: RefreshLinkUrlHandler;
	onConnectSuccess?: () => void;
};

const ConnectButton = ({ item, refreshLinkUrl, onConnectSuccess }: ConnectButtonProps) => {
	const { formatMessage } = useIntl();
	const { connect, isConnecting } = useConnectSmartLinkAccount({
		item,
		refreshLinkUrl,
		onConnectSuccess,
	});

	return (
		<Box xcss={styles.trailing}>
			<Pressable
				xcss={isConnecting ? pressableStyles.connecting : pressableStyles.connect}
				onClick={connect}
				isDisabled={isConnecting}
			>
				{formatMessage(isConnecting ? i18n.connecting : i18n.connect)}
			</Pressable>
		</Box>
	);
};

export type LinkDirection = 'incoming' | 'outgoing';

export type ConnectionsByLineMenuItemProps = {
	item: EnhancedSmartLink;
	refreshLinkUrl: RefreshLinkUrlHandler;
	linkDirection: LinkDirection;
	onConnectSuccess?: () => void;
	onHover?: ConnectionItemHandler;
	onNavigate?: () => void;
};

const ConnectionsByLineMenuItemInner = ({
	item,
	refreshLinkUrl,
	linkDirection,
	onConnectSuccess,
	onHover,
	onNavigate,
}: ConnectionsByLineMenuItemProps): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();
	const [isRowHovered, setIsRowHovered] = useState(false);
	const hasPreviewAccess = item.access === 'granted';
	const actions = useSmartLinkActions({
		url: item.url,
		appearance: 'inline',
		prefetch: hasPreviewAccess,
	});

	const previewAction = actions.find((action) => action.id === PREVIEW_ACTION_ID);

	// The platform only derives a `preview-content` action when the link's
	// SmartCard store entry carries an embed `data.preview`. Same-site Confluence
	// pages frequently resolve to a preview-less entry (SSR/minimal/metadata-only
	// resolution), so they never get the action and the row shows no preview
	// affordance — unlike Jira/3P links, which resolve with the embed. A full
	// reload makes ORS return the embed, after which useSmartLinkActions
	// re-derives the action and the row gains the same preview icon + embed modal
	// as every other product.
	const reloadLink = useSmartLinkReload({ url: item.url });
	const hasRequestedPreviewResolveRef = useRef(false);

	// Trigger that reload up front — as soon as the row mounts (the menu opens) —
	// rather than on hover, so the preview icon is ready before the user reaches
	// the row. Gated only on the row being granted and currently exposing no
	// preview action. We deliberately do NOT gate on `actions.length`: same-site
	// Confluence pages resolve to a metadata-only store entry (just @type/name/
	// url/generator) that yields ZERO actions, so a resolved-but-preview-less row
	// is indistinguishable from a still-resolving one by action count — gating on
	// it would block the reload for exactly the rows that need it. The byline only
	// renders rows for links already resolved by the page, so there is no in-flight
	// minimal resolve to clobber the full reload's result. Fires at most once per
	// row to avoid redundant resolves and resolve loops.
	useEffect(() => {
		if (hasPreviewAccess && !previewAction && !hasRequestedPreviewResolveRef.current) {
			hasRequestedPreviewResolveRef.current = true;
			reloadLink();
		}
	}, [hasPreviewAccess, previewAction, reloadLink]);

	const fireItemClickAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'menuItem',
				actionSubjectId: MENU_ITEM_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
				attributes: {
					product: item.product,
					resourceType: item.resourceType,
					access: item.access,
					occurrenceIndex: item.occurrenceIndex,
					isLivePage,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isLivePage, item]);

	const fireScrollResultAnalytics = useCallback(
		(result: SmartLinkScrollResult) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: result === 'success' ? 'succeeded' : 'failed',
					actionSubject: 'smartLinkScroll',
					actionSubjectId: SCROLL_RESULT_ACTION_SUBJECT_ID,
					source: ANALYTICS_SOURCE,
					attributes: {
						result,
						reason: result === 'success' ? undefined : result,
						product: item.product,
						resourceType: item.resourceType,
						occurrenceIndex: item.occurrenceIndex,
						isLivePage,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isLivePage, item],
	);

	const handleRowClick = useCallback(() => {
		fireItemClickAnalytics();
		if (linkDirection === 'incoming') {
			window.open(item.url, '_blank', 'noopener,noreferrer');
			onNavigate?.();
			return;
		}

		const root = getSmartLinkContentRoot(isLivePage);
		if (!root) {
			fireScrollResultAnalytics('not_found');
			return;
		}

		const result = findRenderedSmartLinkElement({
			root,
			url: item.url,
		});

		if (result.status !== 'found') {
			fireScrollResultAnalytics(result.status);
			return;
		}

		try {
			scrollToElement(result.element, undefined, 'smooth', isLivePage);
			fireScrollResultAnalytics('success');
			onNavigate?.();
		} catch {
			fireScrollResultAnalytics('scroll_error');
		}
	}, [
		fireItemClickAnalytics,
		fireScrollResultAnalytics,
		isLivePage,
		item,
		linkDirection,
		onNavigate,
	]);

	const handleMouseEnter = useCallback(() => {
		setIsRowHovered(true);
		onHover?.(item);
	}, [onHover, item]);

	const handleMouseLeave = useCallback(() => {
		setIsRowHovered(false);
	}, []);

	const handlePreviewClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'connectionsBylinePreviewButton',
					source: 'connectionsBylineMenu',
					attributes: {
						product: item.product,
						resourceType: item.resourceType,
					},
				},
			}).fire();

			void previewAction?.invoke().catch(reportPreviewActionError);
		},
		[createAnalyticsEvent, item.product, item.resourceType, previewAction],
	);

	const showPreviewAction = hasPreviewAccess && Boolean(previewAction);
	// `forbidden` means the user is authenticated to the provider but lacks
	// permission to this specific resource (e.g. a private Google Drive doc) —
	// connecting an account can't grant access, so don't offer Connect. (Note:
	// `meta.visibility` is unreliable here: accessible docs report 'restricted'
	// while the private one reports 'not_found', so we key on `access`.)
	const showConnectAction =
		!hasPreviewAccess && item.access !== 'forbidden' && item.authProviders?.length > 0;

	// Authorized links use the data from ORS. For unauthorized links we
	// display the hostname as the description and the rest of the URL as
	// the title.
	const { titleContent, descriptionContent } = hasPreviewAccess
		? getGrantedRowContent(item, locale, formatMessage)
		: getNonGrantedRowContent(item.url);
	const icon = getSmartLinkIcon(item);

	const rowContent = (
		<Inline space="space.150" alignBlock="center">
			{icon ? (
				<Tile label="" size="small" backgroundColor="transparent" hasBorder>
					<SmartLinkIconRenderer icon={icon} size="menu" />
				</Tile>
			) : null}
			<Box xcss={styles.textColumn}>
				<Stack space="space.025">
					<Box xcss={styles.title}>{titleContent}</Box>
					<Box xcss={styles.description}>{descriptionContent}</Box>
				</Stack>
			</Box>
			{showConnectAction ? (
				<ConnectButton
					item={item}
					refreshLinkUrl={refreshLinkUrl}
					onConnectSuccess={onConnectSuccess}
				/>
			) : null}
			{showPreviewAction && isRowHovered ? (
				<Box xcss={styles.trailing}>
					<ConnectionsByLinePreviewButton
						label={formatMessage(i18n.openPreview)}
						onClick={handlePreviewClick}
					/>
				</Box>
			) : null}
		</Inline>
	);

	const customItem = (triggerRef?: React.Ref<HTMLDivElement>) => (
		<CustomItem<RowComponentProps>
			component={RowComponent}
			ref={triggerRef}
			onClick={handleRowClick}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{rowContent}
		</CustomItem>
	);

	if (showConnectAction) {
		return (
			<UnconnectedConnectorHoverCardLoadable item={item} isOpen={isRowHovered}>
				{({ ref }) => customItem(ref as React.Ref<HTMLDivElement>)}
			</UnconnectedConnectorHoverCardLoadable>
		);
	}

	return customItem();
};

export const ConnectionsByLineMenuItem: React.MemoExoticComponent<
	({
		item,
		refreshLinkUrl,
		linkDirection,
		onConnectSuccess,
		onHover,
		onNavigate,
	}: ConnectionsByLineMenuItemProps) => React.JSX.Element
> = memo(ConnectionsByLineMenuItemInner);
